import { FC, useState } from "react";
import { ILocalDate } from "../../../../entities/calendar";
import { IScheduleDateOverride, ISlotPattern, TWeekday } from "../../../../entities/schedule";
import { MyInput } from "../../../../shared/ui/input";
import { MyTextarea } from "../../../../shared/ui/myTextarea";
import { addDaysToLocalDate, getDateInputValue, getLocalDateFromInput, getTodayLocalDate } from "../../lib/scheduleDate";
import { ScheduleWeekGrid } from "../weekGrid/ScheduleWeekGrid";
import { createUtcDateFromMoscow, getMoscowDateTimeParts } from "../../../../shared/lib/helpers/moscowDateTime";
import classes from './scheduleDateOverrides.module.scss'

interface IProps {
    value: IScheduleDateOverride[];
    onChange: (value: IScheduleDateOverride[]) => void;
    editable: boolean;
}

const WEEKDAY_BY_DATE_INDEX: Record<number, TWeekday> = {
    0: 'WEEKDAY_SUNDAY',
    1: 'WEEKDAY_MONDAY',
    2: 'WEEKDAY_TUESDAY',
    3: 'WEEKDAY_WEDNESDAY',
    4: 'WEEKDAY_THURSDAY',
    5: 'WEEKDAY_FRIDAY',
    6: 'WEEKDAY_SATURDAY',
}

const getDateKey = (date: ILocalDate): string => getDateInputValue(date)

const getWeekday = (date: ILocalDate): TWeekday => {
    const dayIndex = new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay()
    return WEEKDAY_BY_DATE_INDEX[dayIndex]
}

const getMoscowSlotIndex = (startsAt: string): number => {
    const {hours, minutes} = getMoscowDateTimeParts(startsAt)

    return hours * 2 + Math.floor(minutes / 30)
}

const getPatterns = (override: IScheduleDateOverride): ISlotPattern[] => {
    const weekday = getWeekday(override.date)

    return override.availableSlots.map(slot => ({
        weekday,
        startSlot: getMoscowSlotIndex(slot.startsAt),
        durationSlots: slot.durationSlots,
    }))
}

const getStartsAt = (date: ILocalDate, startSlot: number): string => {
    const hours = Math.floor(startSlot / 2)
    const minutes = startSlot % 2 === 0 ? 0 : 30

    return createUtcDateFromMoscow(date.year, date.month, date.day, hours, minutes).toISOString()
}

const getAvailableSlots = (date: ILocalDate, patterns: ISlotPattern[]): IScheduleDateOverride['availableSlots'] => {
    return patterns.map(pattern => ({
        startsAt: getStartsAt(date, pattern.startSlot),
        durationSlots: pattern.durationSlots,
    }))
}

const MAX_PERIOD_DAYS = 120

const getNextFreeDate = (overrides: IScheduleDateOverride[]): ILocalDate => {
    const existingDates = new Set(overrides.map(item => getDateKey(item.date)))
    const date = getTodayLocalDate()

    while(existingDates.has(getDateKey(date))){
        const nextDate = new Date(date.year, date.month - 1, date.day + 1)
        date.year = nextDate.getFullYear()
        date.month = nextDate.getMonth() + 1
        date.day = nextDate.getDate()
    }

    return date
}

const compareDateKeys = (first: ILocalDate, second: ILocalDate): number => {
    return getDateKey(first).localeCompare(getDateKey(second))
}

const getDatesInRange = (from: ILocalDate, to: ILocalDate): ILocalDate[] => {
    const dates: ILocalDate[] = []
    let current = from

    while(compareDateKeys(current, to) <= 0){
        dates.push({...current})
        current = addDaysToLocalDate(current, 1)
    }

    return dates
}

type TOverrideDisplayItem =
    | {type: 'single'; override: IScheduleDateOverride; index: number}
    | {type: 'period'; startDate: ILocalDate; endDate: ILocalDate; comment: string; indexes: number[]; key: string}

const formatDateLabel = (date: ILocalDate): string => {
    const [year, month, day] = getDateKey(date).split('-')

    return `${day}.${month}.${year}`
}

const formatDateRangeLabel = (from: ILocalDate, to: ILocalDate): string => {
    if(getDateKey(from) === getDateKey(to)) return formatDateLabel(from)

    return `${formatDateLabel(from)}–${formatDateLabel(to)}`
}

const getDisplayItems = (overrides: IScheduleDateOverride[]): TOverrideDisplayItem[] => {
    const sortedItems = overrides
        .map((override, index) => ({override, index}))
        .sort((first, second) => compareDateKeys(first.override.date, second.override.date))
    const items: TOverrideDisplayItem[] = []
    let periodItems: typeof sortedItems = []

    const flushPeriod = () => {
        if(!periodItems.length) return

        if(periodItems.length === 1){
            items.push({type: 'single', override: periodItems[0].override, index: periodItems[0].index})
        }
        else{
            const startDate = periodItems[0].override.date
            const endDate = periodItems[periodItems.length - 1].override.date
            const indexes = periodItems.map(item => item.index)

            items.push({
                type: 'period',
                startDate,
                endDate,
                comment: periodItems[0].override.comment,
                indexes,
                key: `${getDateKey(startDate)}-${getDateKey(endDate)}-${periodItems[0].override.comment}`,
            })
        }

        periodItems = []
    }

    sortedItems.forEach(item => {
        const isFullDay = item.override.availableSlots.length === 0
        const lastPeriodItem = periodItems[periodItems.length - 1]
        const isNextPeriodDay = lastPeriodItem
            && lastPeriodItem.override.comment === item.override.comment
            && getDateKey(addDaysToLocalDate(lastPeriodItem.override.date, 1)) === getDateKey(item.override.date)

        if(isFullDay && (!periodItems.length || isNextPeriodDay)){
            periodItems.push(item)
            return
        }

        flushPeriod()

        if(isFullDay){
            periodItems.push(item)
        }
        else{
            items.push({type: 'single', override: item.override, index: item.index})
        }
    })

    flushPeriod()

    return items
}

export const ScheduleDateOverrides: FC<IProps> = ({value, onChange, editable}) => {

    const todayDateValue = getDateInputValue(getTodayLocalDate())
    const [expandedIndexes, setExpandedIndexes] = useState<number[]>([])
    const [periodFrom, setPeriodFrom] = useState(todayDateValue)
    const [periodTo, setPeriodTo] = useState(todayDateValue)
    const [periodComment, setPeriodComment] = useState('')
    const [periodError, setPeriodError] = useState('')
    const displayItems = getDisplayItems(value)

    const updateOverride = (index: number, nextOverride: IScheduleDateOverride) => {
        onChange(value.map((item, itemIndex) => itemIndex === index ? nextOverride : item))
    }

    const addOverride = () => {
        const nextOverride: IScheduleDateOverride = {
            date: getNextFreeDate(value),
            availableSlots: [],
            comment: '',
        }

        onChange([...value, nextOverride])
        setExpandedIndexes(indexes => [...indexes, value.length])
    }

    const addPeriodOverride = () => {
        setPeriodError('')

        if(!periodFrom || !periodTo){
            setPeriodError('Выберите начало и окончание периода')
            return
        }

        if(periodFrom > periodTo){
            setPeriodError('Дата окончания не может быть раньше даты начала')
            return
        }

        const from = getLocalDateFromInput(periodFrom)
        const to = getLocalDateFromInput(periodTo)
        const dates = getDatesInRange(from, to)

        if(dates.length > MAX_PERIOD_DAYS){
            setPeriodError(`Период не должен быть больше ${MAX_PERIOD_DAYS} дней`)
            return
        }

        const periodDateKeys = new Set(dates.map(getDateKey))
        const periodOverrides: IScheduleDateOverride[] = dates.map(date => ({
            date,
            availableSlots: [],
            comment: periodComment,
        }))
        const nextOverrides = [
            ...value.filter(item => !periodDateKeys.has(getDateKey(item.date))),
            ...periodOverrides,
        ].sort((first, second) => compareDateKeys(first.date, second.date))

        onChange(nextOverrides)
        setExpandedIndexes([])
        setPeriodComment('')
    }

    const removeOverride = (index: number) => {
        onChange(value.filter((_, itemIndex) => itemIndex !== index))
        setExpandedIndexes(indexes => indexes
            .filter(itemIndex => itemIndex !== index)
            .map(itemIndex => itemIndex > index ? itemIndex - 1 : itemIndex)
        )
    }

    const removePeriod = (indexes: number[]) => {
        const indexSet = new Set(indexes)

        onChange(value.filter((_, itemIndex) => !indexSet.has(itemIndex)))
        setExpandedIndexes(currentIndexes => currentIndexes
            .filter(itemIndex => !indexSet.has(itemIndex))
            .map(itemIndex => itemIndex - indexes.filter(removedIndex => removedIndex < itemIndex).length)
        )
    }

    const toggleOverride = (index: number) => {
        setExpandedIndexes(indexes => indexes.includes(index)
            ? indexes.filter(itemIndex => itemIndex !== index)
            : [...indexes, index]
        )
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    <h3>Исключения по датам</h3>
                    <p>
                        На выбранную дату действует только этот набор доступных слотов. Остальное время будет недоступно.
                        Если не выбрать слоты — весь день будет выходным.
                    </p>
                </section>
                {editable && (
                    <button type="button" className={classes.addButton} onClick={addOverride}>Добавить день</button>
                )}
            </section>

            {editable && (
                <section className={classes.periodPanel}>
                    <section>
                        <h4>Добавить период</h4>
                        <p>На все даты периода исполнитель будет недоступен целый день. Существующие исключения в периоде будут заменены.</p>
                    </section>
                    <section className={classes.periodFields}>
                        <MyInput
                            type="date"
                            title="Дата начала"
                            value={periodFrom}
                            setValue={value => {
                                setPeriodFrom(value)
                                setPeriodError('')
                            }}
                        />
                        <MyInput
                            type="date"
                            title="Дата окончания"
                            value={periodTo}
                            setValue={value => {
                                setPeriodTo(value)
                                setPeriodError('')
                            }}
                        />
                        <MyTextarea
                            title="Комментарий"
                            value={periodComment}
                            setValue={setPeriodComment}
                            placeholder="Например: болезнь или отпуск"
                        />
                    </section>
                    <section className={classes.periodActions}>
                        {periodError && <span className={classes.periodError}>{periodError}</span>}
                        <button type="button" className={classes.addButton} onClick={addPeriodOverride}>Добавить период</button>
                    </section>
                </section>
            )}

            {!value.length && (
                <p className={classes.empty}>Исключений по отдельным датам нет</p>
            )}

            <section className={classes.list}>
                {displayItems.map(item => {
                    if(item.type === 'period'){
                        return (
                            <article className={`${classes.card} ${classes.periodCard}`} key={item.key}>
                                <section>
                                    <span className={classes.periodBadge}>Период</span>
                                    <h4>{formatDateRangeLabel(item.startDate, item.endDate)}</h4>
                                    <p>{`Полный выходной · ${item.indexes.length} дн.`}</p>
                                    {item.comment && <p className={classes.periodComment}>{item.comment}</p>}
                                </section>
                                {editable && (
                                    <section className={classes.cardActions}>
                                        <button type="button" className={classes.removeButton} onClick={() => removePeriod(item.indexes)}>Удалить период</button>
                                    </section>
                                )}
                            </article>
                        )
                    }

                    const {override, index} = item
                    const isExpanded = expandedIndexes.includes(index)
                    const patterns = getPatterns(override)
                    const weekday = getWeekday(override.date)

                    return (
                        <article className={classes.card} key={`${getDateKey(override.date)}-${index}`}>
                            <section className={classes.fields}>
                                <MyInput
                                    type="date"
                                    title="Дата исключения"
                                    value={getDateInputValue(override.date)}
                                    setValue={dateValue => {
                                        const date = getLocalDateFromInput(dateValue)
                                        const nextPatterns = getPatterns(override).map(pattern => ({
                                            ...pattern,
                                            weekday: getWeekday(date),
                                        }))

                                        updateOverride(index, {
                                            ...override,
                                            date,
                                            availableSlots: getAvailableSlots(date, nextPatterns),
                                        })
                                    }}
                                    isLoading={!editable}
                                />
                                <MyTextarea
                                    title="Комментарий"
                                    value={override.comment}
                                    setValue={comment => updateOverride(index, {...override, comment})}
                                    disabled={!editable}
                                />
                            </section>

                            <section className={classes.summary}>
                                <span>{patterns.length ? `Доступных периодов: ${patterns.length}` : 'Полный выходной'}</span>
                                <section className={classes.cardActions}>
                                    <button
                                        type="button"
                                        aria-expanded={isExpanded}
                                        onClick={() => toggleOverride(index)}
                                    >
                                        {isExpanded ? 'Скрыть слоты' : editable ? 'Настроить слоты' : 'Посмотреть слоты'}
                                    </button>
                                    {editable && (
                                        <button type="button" className={classes.removeButton} onClick={() => removeOverride(index)}>Удалить</button>
                                    )}
                                </section>
                            </section>

                            {isExpanded && (
                                <section className={classes.grid}>
                                    <p>Пустой набор означает, что исполнитель недоступен весь день.</p>
                                    <ScheduleWeekGrid
                                        value={patterns}
                                        onChange={nextPatterns => updateOverride(index, {
                                            ...override,
                                            availableSlots: getAvailableSlots(override.date, nextPatterns),
                                        })}
                                        editable={editable}
                                        visibleWeekdays={[weekday]}
                                    />
                                </section>
                            )}
                        </article>
                    )
                })}
            </section>
        </section>
    )
}
